import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { Layout } from './components/Layout/Layout';
import { HomePage } from './pages/Home/HomePage';
import { LoginPage } from './pages/Auth/LoginPage';
import { SignupPage } from './pages/Auth/SignupPage';
import { ForgotPasswordPage } from './pages/Auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/Auth/ResetPasswordPage';
import { OnboardingPage } from './pages/Onboarding/OnboardingPage';
import { DashboardPage } from './pages/Dashboard/DashboardPage';
import { AddProgramPage } from './pages/Dashboard/AddProgramPage';
import { ProgramDetailPage } from './pages/Dashboard/ProgramDetailPage';
import { ExplorePage } from './pages/Explore/ExplorePage';
import { CourseDetailPage } from './pages/Explore/CourseDetailPage';
import { RecommendationsPage } from './pages/Recommendations/RecommendationsPage';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { LegalPage } from './pages/Legal/LegalPage';
import { LanguageTestsPage } from './pages/LanguageTests/LanguageTestsPage';
import { GuidesPage } from './pages/Guides/GuidesPage';
import { GuidePage } from './pages/Guides/GuidePage';
import { AdminReportReviewPage } from './pages/Admin/AdminReportReviewPage';
import { PathPage } from './pages/Path/PathPage';
import { ExperienceBrowsePage } from './pages/Experiences/ExperienceBrowsePage';
import { ExperienceSubmitPage } from './pages/Experiences/ExperienceSubmitPage';
import { AdminExperienceReviewPage } from './pages/Experiences/AdminExperienceReviewPage';
import { Spinner } from './components/Feedback/Spinner';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="h-10 w-10" />
          <span className="text-text-muted text-sm">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="explore" element={<ExplorePage />} />
          <Route path="courses/:id" element={<CourseDetailPage />} />
          <Route path="experiences" element={<ExperienceBrowsePage />} />
          <Route path="language-tests" element={<LanguageTestsPage />} />
          <Route path="guides" element={<GuidesPage />} />
          <Route path="guides/:slug" element={<GuidePage />} />
          <Route path="privacy" element={<LegalPage doc="privacy" />} />
          <Route path="terms" element={<LegalPage doc="terms" />} />
        </Route>
        
        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } />
        
        {/* Protected routes with Layout */}
        <Route path="/dashboard" element={<Layout />}>
          <Route index element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="add" element={
            <ProtectedRoute>
              <AddProgramPage />
            </ProtectedRoute>
          } />
          <Route path="programs/:id" element={
            <ProtectedRoute>
              <ProgramDetailPage />
            </ProtectedRoute>
          } />
        </Route>
        
        <Route path="/path" element={<Layout />}>
          <Route index element={
            <ProtectedRoute>
              <PathPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Recommendations route */}
        <Route path="/recommendations" element={<Layout />}>
          <Route index element={
            <ProtectedRoute>
              <RecommendationsPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Experiences route */}
        <Route path="/experiences/share" element={<Layout />}>
          <Route index element={
            <ProtectedRoute>
              <ExperienceSubmitPage />
            </ProtectedRoute>
          } />
        </Route>

        <Route path="/experiences/share/:programId" element={<Layout />}>
          <Route index element={
            <ProtectedRoute>
              <ExperienceSubmitPage />
            </ProtectedRoute>
          } />
        </Route>

        <Route path="/admin/experiences" element={<Layout />}>
          <Route index element={
            <ProtectedRoute>
              <AdminExperienceReviewPage />
            </ProtectedRoute>
          } />
        </Route>
        <Route path="/admin/reports" element={<Layout />}>
          <Route index element={
            <ProtectedRoute>
              <AdminReportReviewPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Settings route */}
        <Route path="/settings" element={<Layout />}>
          <Route index element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
        </Route>
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <AppRoutes />
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
